import type {
  SolarSystemPricingDto,
  SolarSystemPricingMarginDto,
  SolarSystemPricingSetupGapDto,
  SolarSystemPricingTotalsDto,
} from "~/api/dto/solar-proposal-system-pricing.dto"
import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import { getSolarProposalErrorStatus } from "~/api/hooks/useSolarProposal"
import useSolarProposalSystemPricing from "~/api/hooks/useSolarProposalSystemPricing"
import useUpdateSolarProposalMargin from "~/api/hooks/useUpdateSolarProposalMargin"
import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/pricing-errors"
import { formatCurrency } from "~/utils/finance/format"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  solarPricingErrorMessage,
  solarPricingRefusal,
} from "./solar-pricing-error"
import { SOLAR_SETUP_STEP_LABELS, solarSetupStepPath } from "./solar-setup-paths"
import { SolarLockedNote } from "./SolarLockedNote"
import { previewTotalCostForMargin } from "../ProposalPresentation/pricing/preview-total-cost-for-margin"
import { PricingOutOfDateNotice } from "../ProposalPresentation/pricing/pricing-out-of-date"
import {
  PricingSetupGaps,
  SaveOrRefreshPricingFooter,
  type PricingSetupGapLink,
} from "../ProposalPresentation/pricing/system-pricing-actions"
import {
  MarginField,
  useMarginDraft,
  type MarginDraftRules,
} from "../ProposalPresentation/pricing/system-pricing-margin"
import {
  formatPricePerWatt,
  PricingBreakdownTable,
  PricingGroupsTable,
  PricingTotals,
  type PricingBreakdownGroup,
} from "../ProposalPresentation/pricing/system-pricing-table"

export const SOLAR_PREVIEW_TOTAL_NOTE = `Estimated until pricing refreshes`
export const CLASSIFICATION_MISSING_PROMPT = `Refresh Pricing to see the full breakdown`

const SAVE_MARGIN_ERROR = `Unable to save margin`

/** A 400 names the bound, a 403 or 409 is the server's reason; else generic. */
const describeSaveError = (error: unknown): string => {
  const status = getSolarProposalErrorStatus(error)
  return status === 400 || status === 403 || status === 409
    ? parseAxiosError(error, SAVE_MARGIN_ERROR)
    : SAVE_MARGIN_ERROR
}

const formatMarginBound = (value: number): string => `$${value.toFixed(2)}/W`

/** The margin as the draft first shows it: two decimals, up to four. */
const formatMarginDraft = (value: number): string =>
  value.toLocaleString(`en-US`, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
    useGrouping: false,
  })

/** The server's bounds, mirrored so the seller sees a bad draft at once. */
const solarMarginRules = (
  margin: SolarSystemPricingMarginDto,
): MarginDraftRules => ({
  decimals: 4,
  min: margin.min,
  max: margin.max,
  messages: {
    invalid: `Enter a margin in dollars per watt with at most four decimal places.`,
    tooLow: `Margin too low; must be at least ${formatMarginBound(margin.min)}`,
    tooHigh: `Margin too high; must not exceed ${formatMarginBound(margin.max)}`,
  },
  format: formatMarginDraft,
})

const toGapLink = (
  proposalId: string,
  gap: SolarSystemPricingSetupGapDto,
): PricingSetupGapLink => ({
  key: gap.key,
  label: gap.label,
  href: gap.step != null ? solarSetupStepPath(proposalId, gap.step) : null,
  action: gap.step != null ? `Open ${SOLAR_SETUP_STEP_LABELS[gap.step]}` : null,
})

/** PowurLine's own figures and, when the server sends it, Enterprise Info. */
const powurLineGroups = (
  view: SolarSystemPricingDto,
): PricingBreakdownGroup[] => {
  const groups: PricingBreakdownGroup[] = []
  if (view.powurLine != null) {
    groups.push({
      key: `powurline`,
      label: `PowurLine`,
      rows: [
        {
          key: `powurline-price`,
          label: `PowurLine Price`,
          amount: view.powurLine.sellerPrice,
          ppw: view.powurLine.sellerPpw,
          namesOnly: false,
        },
        {
          key: `total-adders`,
          label: `Total Adders`,
          amount: view.powurLine.totalAdders,
          ppw: view.powurLine.totalAddersPpw,
          namesOnly: false,
        },
      ],
    })
  }
  if (view.enterpriseInfo != null) {
    groups.push({
      key: `enterprise-info`,
      label: `Enterprise Info`,
      rows: [
        {
          key: `enterprise-powurline-price`,
          label: `Enterprise PowurLine Price`,
          amount: view.enterpriseInfo.baselineAmount,
          ppw: view.enterpriseInfo.baselinePpw,
          namesOnly: false,
        },
        {
          key: `enterprise-commission`,
          label: `Enterprise Commission`,
          amount: view.enterpriseInfo.commissionAmount,
          ppw: view.enterpriseInfo.commissionPpw,
          namesOnly: false,
        },
      ],
    })
  }
  return groups
}

const CloseFooter = ({ onClose }: { onClose: () => void }): JSX.Element => (
  <div className="mt-6 flex shrink-0 items-center border-t border-border pt-5">
    <button
      type="button"
      onClick={onClose}
      className="rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground"
    >
      Close
    </button>
  </div>
)

const NotCalculated = ({
  proposalId,
  view,
}: {
  proposalId: string
  view: SolarSystemPricingDto
}): JSX.Element => {
  const calculatePricing = useCalculateSolarProposalPricing()
  const disabled =
    view.setupGaps.length > 0 ||
    view.financingLocked ||
    calculatePricing.isLoading

  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="text-[length:var(--step--1)] text-muted">
        No stored pricing
      </p>
      {view.setupGaps.length > 0 ? (
        <PricingSetupGaps
          gaps={view.setupGaps.map((gap) => toGapLink(proposalId, gap))}
        />
      ) : null}
      {calculatePricing.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-danger">
          {solarPricingRefusal(calculatePricing.error) ??
            (isPricingSupersededError(calculatePricing.error)
              ? PRICING_SUPERSEDED_MESSAGE
              : `Unable to calculate pricing`)}
        </p>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          calculatePricing.mutate({ proposalId })
        }}
        className="self-start rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {calculatePricing.isLoading ? `Calculating` : `Calculate Pricing`}
      </button>
    </div>
  )
}

const MarginCell = ({
  margin,
  totals,
  editable,
  draft,
  error,
  disabled,
  onChange,
}: {
  margin: SolarSystemPricingMarginDto
  totals: SolarSystemPricingTotalsDto
  editable: boolean
  draft: string
  error: string | null
  disabled: boolean
  onChange: (value: string) => void
}): JSX.Element => (
  <span className="flex flex-col items-end gap-1">
    {editable ? (
      <MarginField
        unit="$/W"
        draft={draft}
        error={error}
        disabled={disabled}
        onChange={onChange}
      />
    ) : (
      <span>{formatPricePerWatt(margin.current)}</span>
    )}
    <span
      data-slot="pricing-margin-amount"
      className="text-[length:var(--step--2)] text-muted"
    >
      {formatCurrency(totals.margin.amount)}
    </span>
  </span>
)

const StoredSolarSystemPricing = ({
  proposalId,
  view,
  totals,
  onClose,
}: {
  proposalId: string
  view: SolarSystemPricingDto
  totals: SolarSystemPricingTotalsDto
  onClose: () => void
}): JSX.Element => {
  const { editable } = view.margin
  const margin = useMarginDraft({
    saved: view.margin.current,
    seedKey: String(view.margin.current),
    rules: solarMarginRules(view.margin),
  })
  const saveMargin = useUpdateSolarProposalMargin()
  const refreshPricing = useCalculateSolarProposalPricing()
  const busy = saveMargin.isLoading || refreshPricing.isLoading
  const dirty = editable && margin.dirty
  // Only an edit previews, and only against pricing that says what rate and
  // size it used; Save then refreshes for the exact figure.
  const previewTotal =
    dirty &&
    margin.parsed != null &&
    view.margin.pricedRate != null &&
    view.systemSizeWatts != null
      ? previewTotalCostForMargin({
          totalCost: totals.totalCost.amount,
          pricedMargin: view.margin.pricedRate,
          draftMargin: margin.parsed,
          multiplier: view.systemSizeWatts,
        })
      : null
  const extraGroups = powurLineGroups(view)

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Admins and proposals without an enterprise keep their rows on
            legacy pricing; only restricted viewers get totals alone. */}
        {view.classificationMissing && view.groups.length === 0 ? (
          <div data-slot="pricing-body" className="min-h-0 flex-1">
            <p role="status" className="text-[length:var(--step--1)] text-muted">
              {CLASSIFICATION_MISSING_PROMPT}
            </p>
          </div>
        ) : (
          <PricingBreakdownTable groups={view.groups} showPpw>
            {extraGroups.length > 0 ? (
              <PricingGroupsTable
                caption="PowurLine pricing"
                groups={extraGroups}
                showPpw
                className="mt-6"
              />
            ) : null}
          </PricingBreakdownTable>
        )}
        <PricingTotals
          margin={
            <MarginCell
              margin={view.margin}
              totals={totals}
              editable={editable}
              draft={margin.draft}
              error={margin.error}
              disabled={busy}
              onChange={margin.setDraft}
            />
          }
          salesTax={totals.salesTax}
          exciseTax={totals.exciseTax}
          totalCost={previewTotal ?? totals.totalCost.amount}
          totalPpw={previewTotal == null ? totals.totalCost.ppw : null}
          totalNote={previewTotal != null ? SOLAR_PREVIEW_TOTAL_NOTE : null}
        />
        {!editable && view.margin.reason != null ? (
          <div className="mt-3">
            <SolarLockedNote>{view.margin.reason}</SolarLockedNote>
          </div>
        ) : null}
        {view.setupGaps.length > 0 ? (
          <div className="mt-3">
            <PricingSetupGaps
              gaps={view.setupGaps.map((gap) => toGapLink(proposalId, gap))}
            />
          </div>
        ) : null}
      </div>
      <SaveOrRefreshPricingFooter
        save={saveMargin}
        saveVariables={
          margin.parsed != null
            ? { proposalId, body: { marginPerWatt: margin.parsed } }
            : null
        }
        refresh={refreshPricing}
        refreshVariables={{ proposalId }}
        dirty={dirty}
        canEdit={!view.financingLocked}
        blocked={view.setupGaps.length > 0}
        outOfDateNotice={
          view.pricingStatus === `out_of_date` ? (
            <PricingOutOfDateNotice testId="solar-system-pricing-out-of-date" />
          ) : null
        }
        onClose={onClose}
        describeSaveError={describeSaveError}
        describeRefreshError={solarPricingErrorMessage}
      />
    </>
  )
}

/**
 * System Pricing for a Solar Proposal, rendered only from the server's view:
 * its groups (already collapsed or hidden for this viewer), PowurLine and
 * Enterprise Info when sent, and the totals. The $/W margin is editable only
 * when the server says so, within its bounds. Save persists the margin and
 * refreshes pricing; while an edit is unsaved the total is an estimate.
 */
export const SolarSystemPricingStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const query = useSolarProposalSystemPricing(proposalId)

  // A failed refetch keeps the view already on screen.
  if (!query.data) {
    return (
      <>
        {query.isError ? (
          <div className="flex flex-col gap-2">
            <p role="alert" className="text-[length:var(--step--2)] text-danger">
              Unable to load stored pricing
            </p>
            <button
              type="button"
              onClick={() => {
                void query.refetch()
              }}
              className="self-start rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="text-[length:var(--step--1)] text-muted">
            Loading pricing
          </p>
        )}
        <CloseFooter onClose={onClose} />
      </>
    )
  }

  const view = query.data

  return (
    <>
      {view.financingLocked ? (
        <div className="mb-4">
          <SolarLockedNote>
            {view.financingLockReason ?? `Financing locks pricing.`}
          </SolarLockedNote>
        </div>
      ) : null}
      {view.totals == null ? (
        <>
          <NotCalculated proposalId={proposalId} view={view} />
          <CloseFooter onClose={onClose} />
        </>
      ) : (
        <StoredSolarSystemPricing
          proposalId={proposalId}
          view={view}
          totals={view.totals}
          onClose={onClose}
        />
      )}
    </>
  )
}
